// MARK: NPM Modules
import React from "react";
import styled from "styled-components";
// MARK: Redux
import { RootState } from "reduxFunctionality/index";
import { useSelector } from "react-redux";
// MARK: Types
import FooterProps from "types/globals/footer/footerProps";
import ReduxStyleguide from "types/redux/styleguide";
import ButtonType from "types/fields/matrix/common/button";
import NavigationItem from "types/globals/navigationBar/navigationItem";

// MARK: Components
import Link from "components/common/links/standard";
import Typography from "components/common/typography";
import Button from "components/common/button/textButton";
import MailContainer from "components/common/layouts/containers/mailContainer";
import MailIcon from "components/common/mail/icon";
import FooterNavigationItem from "./components/footerNavigationItem";
// MARK: Functionality
import { TrackGoogleAnalyticsEvent } from "utils/google-analytics";
import ContactFormCategory from "utils/google-analytics/events/contact";
import UserPressedEmailUsDirectlyInFooter from "utils/google-analytics/events/contact/userPressedEmailUsDirectlyInFooter";
import CSSStyles from "enums/cssStyles";
import WAIAriaRole from "types/accessibility/roles";

// MARK: Styled Components
interface FooterContainerProps {
  maxWidth: number;
}
const Container = styled.footer<FooterContainerProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth}px;
  height: auto;
  margin: auto;
  margin-top: 100px;
  margin-bottom: 100px;
  overflow: hidden;
`;

interface SocialMediaContainerProps {
  margin: number;
}

const SocialMediaContainer = styled.div<SocialMediaContainerProps>`
  display: flex;
  height: 40px;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 1439px) {
    width: ${({ margin }) => "calc(100% - " + 2*margin +"px)"};
    margin: auto;
  }

  @media (min-width: 1440px) {
    width: 100%;
  }

`;

interface SocialMediaBarProps {
  backgroundcolor: string;
}

const SocialMediaBar = styled.div<SocialMediaBarProps>`
  width: calc(50% - 50px);
  height: 1px;
  background-color: ${({ backgroundcolor }) => backgroundcolor};
`;

const SocialMediaContent = styled.div`
  display: flex;
  width: 80px;
  height: 40px;
  justify-content: space-around;
  align-items: center;
`;

const SocialMediaIcon = styled(({ ...props }) => <img {...props} />)`
  width: 30px;
  height: 30px;
`;

const CopyrightContainer = styled.div`
  display: flex;
  width: 100%;
  height: 160px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Logo = styled(({ ...props }) => <img alt="delasign" {...props} />)`
  width: 40px;
  height: 40px;
`;

const InformationContainer = styled.div`
  display: flex;
  width: 100%;
  height: auto;
  justify-content: flex-start;
  align-items: flex-start;

  @media (max-width: 1024px) {  
    flex-direction: column;
  }

  @media (min-width: 1025px) {
    flex-direction: row;
  }
`;

const Information = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 20px;
`;

const FooterNavigationList = styled.ul`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  min-height: 48px;
  padding: 0px;
`;

// MARK: React Component
// Styleguide & Datapoints
const getDataPoints = (styleguide: ReduxStyleguide) => {
  const { colors, mobile } = styleguide;
  const { primary, secondary } = colors;
  return {
    headline: CSSStyles.bodyBold,
    copy: mobile ? CSSStyles.small : CSSStyles.body,
    primary: primary,
    secondary: secondary,
  };
};
// Component
const Footer = ({ data, navigationBarData }: FooterProps) => {
  // MARK: React Redux Variables
  const styleguide: ReduxStyleguide = useSelector(
    (state: RootState) => state.styleguide
  );
  // MARK: Variables
  const { maximumBodyWidth, gutter, margin } = styleguide;
  const { headline, copy, primary, secondary } = getDataPoints(styleguide);
  // MARK: Functionality

  const socialMediaDivider = () => {
    return (
      <SocialMediaContainer margin={margin}>
        <SocialMediaBar backgroundcolor={primary} />
        <SocialMediaContent>
          <a
            href="https://www.instagram.com/delasign"
            target="_blank"
            rel="noopener noreferrer"
          >
            <SocialMediaIcon
              src="/Instagram.webp"
              alt="Select to go to the delasign Instagram"
            />
          </a>
          <a
            href="https://www.linkedin.com/company/delasign"
            target="_blank"
            rel="noopener noreferrer"
          >
            <SocialMediaIcon
              src="/LinkedIn.webp"
              alt="Select to go to the delasign LinkedIn"
            />
          </a>
        </SocialMediaContent>
        <SocialMediaBar backgroundcolor={primary} />
      </SocialMediaContainer>
    );
  };

  const copyright = () => {
    return (
      <CopyrightContainer>
        <Logo src="/delasignBlack.webp" alt="delasign" />
        <br />
        <p>copyright © delasign® llc</p>
      </CopyrightContainer>
    );
  };

  const headquarters = () => {
    return (
      <Information>
        <Typography className={headline} color={primary} textAlign={"left"}>
          Explore Our Site
        </Typography>
        <FooterNavigationList role="menu">{renderNavigationLinks()}</FooterNavigationList>
      </Information>
    );
  };

  const renderNavigationLinks = () => {
    var links: any = [];
    navigationBarData.navigationItems.forEach((element, index) => {
      links.push(
        <FooterNavigationItem
          key={"footer-navigation-item-" + index}
          item={element.pages[0]}
          activeRoute={typeof window === "undefined" ? "" : window.location.pathname}
        />
      );
    });
    return links;
  };

  const offices = () => {
    return (
      <Information>
        <Typography className={headline} color={primary} textAlign={"left"}>
          {data.addressTwoHeadline}
        </Typography>
        <Typography
          className={copy}
          color={secondary}
          textAlign={"left"}
          dangerouslySetInnerHTML
          formatCopy
        >
          {data.addressTwoDetail}
        </Typography>
      </Information>
    );
  };

  const contact = () => {
    return (
      <Information>
        <Typography className={headline} color={primary} textAlign={"left"}>
          {data.contactHeader}
        </Typography>
        <MailContainer
          margintop={0}
          onClick={() => {
            TrackGoogleAnalyticsEvent(
              ContactFormCategory,
              UserPressedEmailUsDirectlyInFooter,
              typeof window === "undefined" ? "" : window.location.pathname
            );
          }}
        >
          <MailIcon />
          <Typography
            marginLeft={gutter}
            className={copy}
            color={secondary}
            textAlign={"left"}
            formatCopy
            dangerouslySetInnerHTML
          >
            {data.contactDetail}
          </Typography>
        </MailContainer>
        <br />
        <Typography className={headline} color={secondary} textAlign={"left"}>
          {data.contactButtonsHeadline}
        </Typography>
        {data.contactButtons.map((buttonData) => {
          return (
            <Button
              key={"legal-button-" + buttonData.buttonTitle}
              page={{
                sectionHandle: "internal",
                uri: buttonData.page[0].uri,
              }}
              type={buttonData.buttonType}
            >
              {buttonData.buttonTitle}
            </Button>
          );
        })}
      </Information>
    );
  };

  const legal = () => {
    return (
      <Information>
        <Typography className={headline} color={primary} textAlign={"left"}>
          {data.legalButtonsHeadline}
        </Typography>
        {data.legalButtons.map((buttonData: ButtonType) => {
          return (
            <Button
              key={"legal-button-" + buttonData.buttonTitle}
              page={{
                sectionHandle: "internal",
                uri: buttonData.page[0].uri,
              }}
              type={buttonData.buttonType}
            >
              {buttonData.buttonTitle}
            </Button>
          );
        })}
      </Information>
    );
  };
  // MARK: Render
  return (
    <Container
      aria-label={"footer"}
      itemScope
      itemProp="https://schema.org/WPFooter"
      maxWidth={maximumBodyWidth}
    >
      {socialMediaDivider()}
      {copyright()}
      <InformationContainer >
        {headquarters()}
        {offices()}
        {contact()}
        {legal()}
      </InformationContainer>
    </Container>
  );
};

export default Footer;
